/**
 * Carrusel de productos recomendados basado en metafields
 * Lógica: Si un producto tiene categoria_de_producto y coleccion,
 * mostrar productos de otra categoría pero de la misma colección
 */

console.log('CartRecommendedProducts: Script cargado correctamente');

class CartRecommendedProducts extends HTMLElement {
  constructor() {
    super();
    this.cartItems = [];
    this.recommendedProducts = [];
    this.limit = 10;
    this._initialized = false;
  }

  connectedCallback() {
    console.log('CartRecommendedProducts: Componente conectado al DOM');
    // Si es dinámico, no auto-inicializar (será manejado por fetchRecommendationsForProduct)
    if (this.dataset.dynamic === 'true') {
      console.log('CartRecommendedProducts: Elemento dinámico, esperando inicialización manual...');
      return;
    }
    // Pequeño delay para asegurar que el DOM esté listo
    setTimeout(() => this.init(), 50);
  }

  async init() {
    // Evitar inicialización múltiple
    if (this._initialized) {
      console.log('CartRecommendedProducts: Ya inicializado, omitiendo...');
      return;
    }
    
    console.log('CartRecommendedProducts: Inicializando...');
    
    // Marcar como inicializado
    this._initialized = true;
    
    // Obtener handles de productos desde el atributo data
    const productHandles = this.dataset.productHandles;
    const coleccion = this.dataset.coleccion;
    const categoriaExcluida = this.dataset.categoriaExcluida;
    
    console.log('CartRecommendedProducts: Datos del componente', {
      productHandles,
      coleccion,
      categoriaExcluida,
      hasHandles: !!productHandles && productHandles.trim() !== ''
    });
    
    // Si es dinámico, no ocultar aunque no haya handles (serán cargados después)
    if (this.dataset.dynamic === 'true') {
      console.log('CartRecommendedProducts: Elemento dinámico, productos serán cargados externamente');
      return;
    }
    
    if (!productHandles || productHandles.trim() === '') {
      console.log('CartRecommendedProducts: No hay productos recomendados (handles vacíos)');
      this.hide();
      return;
    }
    
    // Obtener productos recomendados
    await this.fetchRecommendedProducts(productHandles);
    this.renderProducts();
  }

  async fetchRecommendedProducts(productHandlesString) {
    try {
      const handles = productHandlesString.split(',').map(h => h.trim()).filter(h => h);
      
      if (handles.length === 0) {
        console.log('CartRecommendedProducts: No hay handles de productos');
        this.recommendedProducts = [];
        return;
      }
      
      console.log('CartRecommendedProducts: Buscando productos con handles:', handles);
      
      // Obtener detalles de cada producto
      const products = [];
      const promises = handles.slice(0, this.limit).map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) {
            console.warn(`CartRecommendedProducts: Producto ${handle} no encontrado (${response.status})`);
            return null;
          }
          
          const product = await response.json();
          
          // Verificar que el producto esté disponible y tenga variantes disponibles
          if (!product.available) {
            console.warn(`CartRecommendedProducts: Producto ${handle} no está disponible`);
            return null;
          }
          
          if (!product.variants || product.variants.length === 0) {
            console.warn(`CartRecommendedProducts: Producto ${handle} no tiene variantes`);
            return null;
          }
          
          // Verificar que al menos una variante esté disponible
          const hasAvailableVariant = product.variants.some(variant => variant.available);
          if (!hasAvailableVariant) {
            console.warn(`CartRecommendedProducts: Producto ${handle} no tiene variantes disponibles`);
            return null;
          }
          
          return product;
        } catch (error) {
          console.error(`CartRecommendedProducts: Error obteniendo producto ${handle}:`, error);
          return null;
        }
      });
      
      const results = await Promise.all(promises);
      this.recommendedProducts = results.filter(p => p !== null);
      
      console.log(`CartRecommendedProducts: ${this.recommendedProducts.length} productos encontrados de ${handles.length} handles`);
      
    } catch (error) {
      console.error('CartRecommendedProducts: Error fetching recommended products:', error);
      this.recommendedProducts = [];
    }
  }


  renderProducts() {
    const container = this.querySelector('.cart-recommended-products__carousel');
    const loading = this.querySelector('.cart-recommended-products__loading');
    const empty = this.querySelector('.cart-recommended-products__empty');
    const mainContainer = this.querySelector('.cart-recommended-products__container');
    
    if (!container) {
      console.error('CartRecommendedProducts: No se encontró el contenedor del carrusel');
      return;
    }
    
    // Ocultar loading
    if (loading) {
      loading.style.display = 'none';
    }
    
    if (this.recommendedProducts.length === 0) {
      console.log('CartRecommendedProducts: No hay productos para mostrar');
      if (empty) {
        empty.style.display = 'block';
      }
      this.hide();
      return;
    }
    
    console.log(`CartRecommendedProducts: Renderizando ${this.recommendedProducts.length} productos`);
    
    // Mostrar contenedor principal
    if (mainContainer) {
      mainContainer.style.display = 'block';
    }
    
    // Limpiar contenedor
    container.innerHTML = '';
    
    // Renderizar productos
    this.recommendedProducts.forEach(product => {
      const slide = document.createElement('div');
      slide.className = 'carousel__slide';
      slide.innerHTML = this.renderProductCard(product);
      container.appendChild(slide);
    });
    
    // Agregar event listeners a los formularios de agregar al carrito
    this.setupAddToCartForms();
    
    // Inicializar navegación del carrusel
    this.initCarouselNavigation();
  }

  initCarouselNavigation() {
    // Evitar inicialización múltiple
    if (this._navigationInitialized) {
      return;
    }
    
    const carousel = this.querySelector('.cart-recommended-products__carousel');
    const prevButton = this.querySelector('.cart-recommended-products__nav-button--prev');
    const nextButton = this.querySelector('.cart-recommended-products__nav-button--next');
    const navigation = this.querySelector('.cart-recommended-products__navigation');
    
    if (!carousel || !prevButton || !nextButton) {
      console.log('CartRecommendedProducts: Elementos de navegación no encontrados', {
        carousel: !!carousel,
        prevButton: !!prevButton,
        nextButton: !!nextButton
      });
      return;
    }
    
    // Marcar como inicializado
    this._navigationInitialized = true;
    
    // Mostrar navegación si hay más productos de los que se muestran
    const updateNavigationVisibility = () => {
      const isMobile = window.innerWidth <= 767;
      const visibleCount = isMobile ? 2 : 3;
      
      if (this.recommendedProducts.length > visibleCount) {
        if (navigation) {
          navigation.style.display = 'flex';
        }
      } else {
        if (navigation) {
          navigation.style.display = 'none';
        }
      }
    };
    
    const updateButtonStates = () => {
      // Verificar que el carrusel tenga dimensiones válidas
      if (!carousel || carousel.scrollWidth === 0 || carousel.clientWidth === 0) {
        return;
      }
      
      const scrollLeft = carousel.scrollLeft;
      const scrollWidth = carousel.scrollWidth;
      const clientWidth = carousel.clientWidth;
      const scrollRight = scrollWidth - scrollLeft - clientWidth;
      
      // Usar un umbral más generoso para evitar problemas de precisión
      prevButton.disabled = scrollLeft <= 5;
      nextButton.disabled = scrollRight <= 5;
    };
    
    // Función para hacer scroll
    const scrollCarousel = (direction) => {
      // Verificar que el carrusel esté listo
      if (!carousel || carousel.scrollWidth === 0) {
        console.warn('CartRecommendedProducts: Carrusel no está listo para hacer scroll');
        return;
      }
      
      const isMobile = window.innerWidth <= 767;
      const firstSlide = carousel.querySelector('.carousel__slide');
      
      if (!firstSlide) {
        console.warn('CartRecommendedProducts: No se encontró ningún slide');
        return;
      }
      
      // Esperar a que el slide tenga dimensiones válidas
      const slideWidth = firstSlide.offsetWidth || firstSlide.getBoundingClientRect().width;
      
      if (!slideWidth || slideWidth === 0) {
        console.warn('CartRecommendedProducts: El slide no tiene ancho válido', slideWidth);
        // Reintentar después de un breve delay
        setTimeout(() => scrollCarousel(direction), 100);
        return;
      }
      
      const gap = isMobile ? 12 : 16; // 0.75rem = 12px, 1rem = 16px
      const scrollAmount = slideWidth + gap;
      
      if (direction === 'prev') {
        carousel.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
      
      // Actualizar estados después de un breve delay
      setTimeout(updateButtonStates, 400);
    };
    
    // Event listeners para los botones con prevención de eventos por defecto
    const handlePrevClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!prevButton.disabled) {
        scrollCarousel('prev');
      }
    };
    
    const handleNextClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!nextButton.disabled) {
        scrollCarousel('next');
      }
    };
    
    // Remover listeners anteriores si existen (por si se llama múltiples veces)
    prevButton.removeEventListener('click', handlePrevClick);
    nextButton.removeEventListener('click', handleNextClick);
    
    // Agregar listeners
    prevButton.addEventListener('click', handlePrevClick);
    nextButton.addEventListener('click', handleNextClick);
    
    // Touch events para mobile (sin duplicar con click)
    let touchStartTime = 0;
    const handleTouch = (e, handler) => {
      const touchEndTime = Date.now();
      // Solo procesar si no fue un click (diferencia de tiempo > 100ms)
      if (touchEndTime - touchStartTime > 100) {
        e.preventDefault();
        e.stopPropagation();
        handler(e);
      }
    };
    
    prevButton.addEventListener('touchstart', () => { touchStartTime = Date.now(); });
    prevButton.addEventListener('touchend', (e) => handleTouch(e, handlePrevClick));
    
    nextButton.addEventListener('touchstart', () => { touchStartTime = Date.now(); });
    nextButton.addEventListener('touchend', (e) => handleTouch(e, handleNextClick));
    
    // Actualizar estados de los botones al hacer scroll (con debounce)
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateButtonStates, 50);
    };
    
    carousel.addEventListener('scroll', handleScroll, { passive: true });
    
    // Actualizar al cambiar tamaño de ventana
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateNavigationVisibility();
        // Esperar un poco más para que el layout se estabilice
        setTimeout(updateButtonStates, 100);
      }, 250);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Inicializar
    updateNavigationVisibility();
    
    // Esperar a que el DOM y las imágenes estén completamente renderizados
    const initializeAfterRender = () => {
      // Verificar que los slides tengan dimensiones
      const slides = carousel.querySelectorAll('.carousel__slide');
      let allReady = true;
      
      slides.forEach(slide => {
        if (slide.offsetWidth === 0) {
          allReady = false;
        }
      });
      
      if (allReady && slides.length > 0) {
        updateButtonStates();
      } else {
        // Reintentar después de un breve delay
        setTimeout(initializeAfterRender, 100);
      }
    };
    
    // Esperar a que las imágenes se carguen
    const images = carousel.querySelectorAll('img');
    let imagesLoaded = 0;
    const totalImages = images.length;
    
    if (totalImages === 0) {
      // No hay imágenes, inicializar inmediatamente
      setTimeout(initializeAfterRender, 100);
    } else {
      images.forEach(img => {
        if (img.complete) {
          imagesLoaded++;
        } else {
          img.addEventListener('load', () => {
            imagesLoaded++;
            if (imagesLoaded === totalImages) {
              initializeAfterRender();
            }
          });
          img.addEventListener('error', () => {
            imagesLoaded++;
            if (imagesLoaded === totalImages) {
              initializeAfterRender();
            }
          });
        }
      });
      
      // Si todas las imágenes ya están cargadas
      if (imagesLoaded === totalImages) {
        setTimeout(initializeAfterRender, 100);
      }
      
      // Timeout de seguridad
      setTimeout(initializeAfterRender, 1000);
    }
  }

  setupAddToCartForms() {
    const forms = this.querySelectorAll('[data-cart-add-form]');
    forms.forEach(form => {
      form.addEventListener('submit', this.handleAddToCart.bind(this));
    });
  }

  async handleAddToCart(event) {
    event.preventDefault();
    const form = event.target;
    const button = form.querySelector('button[type="submit"]');
    const variantId = form.querySelector('input[name="id"]').value;
    
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }
    
    // Deshabilitar botón
    button.disabled = true;
    button.textContent = 'Agregando...';
    
    try {
      const response = await fetch(`${theme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Disparar evento de actualización del carrito
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
        
        // Mostrar mensaje de éxito
        button.textContent = '¡Agregado!';
        setTimeout(() => {
          button.textContent = 'Añadir al carrito';
          button.disabled = false;
        }, 2000);
      } else {
        throw new Error(data.message || 'Error al agregar al carrito');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = 'Añadir al carrito';
          button.disabled = false;
        }, 2000);
    }
  }

  renderProductCard(product) {
    // Encontrar la primera variante disponible
    let variant = null;
    if (product.variants && product.variants.length > 0) {
      variant = product.variants.find(v => v.available) || product.variants[0];
    }
    
    // Si no hay variante disponible, no deberíamos mostrar este producto
    if (!variant || !variant.available) {
      console.warn('CartRecommendedProducts: Intentando renderizar producto sin variante disponible');
      return '';
    }
    
    const price = variant.price;
    const compareAtPrice = variant.compare_at_price;
    const available = variant.available;
    
    // Obtener imagen del producto
    let imageUrl = '';
    if (product.featured_image) {
      imageUrl = typeof product.featured_image === 'string' 
        ? product.featured_image 
        : product.featured_image.src || product.featured_image;
    } else if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      imageUrl = typeof firstImage === 'string' 
        ? firstImage 
        : firstImage.src || firstImage;
    }
    
    // Si la imagen es muy grande, usar una versión más pequeña
    if (imageUrl && imageUrl.includes('cdn.shopify.com')) {
      // Agregar parámetros para optimizar la imagen
      if (!imageUrl.includes('_')) {
        imageUrl = imageUrl.replace(/\.(jpg|jpeg|png|webp)/i, '_300x300.$1');
      }
    }
    
    // Formatear precio
    const formattedPrice = this.formatMoney(price);
    const formattedComparePrice = compareAtPrice && compareAtPrice > price ? this.formatMoney(compareAtPrice) : '';
    
    // URL del producto
    const productUrl = product.url || `/products/${product.handle}`;
    
    return `
      <div class="cart-recommended-product-card">
        <div class="cart-recommended-product-card__image">
          <a href="${productUrl}" title="${this.escapeHtml(product.title)}">
            ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" class="lazyload">` : '<div class="thb-placeholder">' + (product.title ? product.title.charAt(0) : '') + '</div>'}
          </a>
        </div>
        <div class="cart-recommended-product-card__info">
          <a href="${productUrl}" class="cart-recommended-product-card__title" title="${this.escapeHtml(product.title)}">
            ${this.escapeHtml(product.title)}
          </a>
          <div class="cart-recommended-product-card__price">
            ${formattedComparePrice ? `<span class="price-compare">${formattedComparePrice}</span>` : ''}
            <span class="price-current">${formattedPrice}</span>
          </div>
          <form action="${theme.routes.cart_add_url}" method="post" enctype="multipart/form-data" class="cart-recommended-product-card__form" data-cart-add-form>
            <input type="hidden" name="id" value="${variant ? variant.id : ''}">
            <button type="submit" class="button cart-recommended-product-card__button" ${!available ? 'disabled' : ''}>
              ${available ? 'Añadir al carrito' : 'Agotado'}
            </button>
          </form>
        </div>
      </div>
    `;
  }

  formatMoney(cents) {
    if (typeof theme === 'undefined' || !theme.moneyFormat) {
      // Formato por defecto
      const amount = (cents / 100).toFixed(2);
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    }
    
    // Usar el formato de dinero del tema
    const amount = (cents / 100).toFixed(2);
    let formatted = theme.moneyFormat.replace(/\{\{amount\}\}/g, amount);
    formatted = formatted.replace(/\{\{amount_no_decimals\}\}/g, Math.round(cents / 100).toString());
    return formatted;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  hide() {
    this.style.display = 'none';
  }
}

// Registrar el custom element
if (!customElements.get('cart-recommended-products')) {
  customElements.define('cart-recommended-products', CartRecommendedProducts);
}

// Función para inicializar elementos que se agregan dinámicamente
function initCartRecommendedElements() {
  const elements = document.querySelectorAll('cart-recommended-products');
  console.log('CartRecommendedProducts: Buscando elementos para inicializar, encontrados:', elements.length);
  
  elements.forEach(el => {
    // Ignorar elementos dinámicos - son manejados por fetchRecommendationsForProduct
    if (el.dataset.dynamic === 'true') {
      console.log('CartRecommendedProducts: Elemento dinámico, omitiendo inicialización automática');
      return;
    }
    
    // Si el elemento tiene el método init y no está inicializado
    if (el.init && typeof el.init === 'function') {
      if (!el._initialized) {
        console.log('CartRecommendedProducts: Inicializando elemento desde initCartRecommendedElements');
        el.init();
      } else {
        // Si ya está inicializado pero no tiene productos, puede que los datos cambiaron
        console.log('CartRecommendedProducts: Elemento ya inicializado, verificando contenido...');
        const container = el.querySelector('.cart-recommended-products__container');
        const carousel = el.querySelector('.cart-recommended-products__carousel');
        
        // Si el contenedor está oculto y el carrusel está vacío, reinicializar (solo para no-dinámicos)
        if (container && container.style.display === 'none' && carousel && carousel.children.length === 0) {
          console.log('CartRecommendedProducts: Contenedor vacío, reinicializando...');
          el._initialized = false;
          el._navigationInitialized = false;
          el.init();
        }
      }
    }
  });
}


// Función para obtener productos recomendados del servidor usando Section Rendering API
async function getRecommendationsFromServer() {
  try {
    console.log('CartRecommendedProducts: Obteniendo recomendaciones del servidor via sección...');
    
    // Usar Section Rendering API para obtener la sección cart-recommendations-api
    const response = await fetch('/?sections=cart-recommendations-api', {
      credentials: 'same-origin'
    });
    
    if (!response.ok) {
      console.log('CartRecommendedProducts: Error en respuesta del servidor:', response.status);
      return null;
    }
    
    const sectionsData = await response.json();
    
    if (sectionsData['cart-recommendations-api']) {
      // Parsear el HTML de la sección para extraer el JSON
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionsData['cart-recommendations-api'], 'text/html');
      const jsonScript = doc.querySelector('script[data-cart-recommendations]');
      
      if (jsonScript) {
        try {
          const data = JSON.parse(jsonScript.textContent.trim());
          
          if (data.success && data.products && data.products.length > 0) {
            console.log('CartRecommendedProducts: Productos encontrados del servidor:', data.products.length);
            console.log('CartRecommendedProducts: Colección:', data.first_product?.coleccion, '| Categoría excluida:', data.first_product?.categoria);
            return data.products;
          } else {
            console.log('CartRecommendedProducts: No hay productos en la respuesta del servidor');
            console.log('CartRecommendedProducts: found_count:', data.found_count, '| coleccion:', data.first_product?.coleccion);
          }
        } catch (parseError) {
          console.log('CartRecommendedProducts: Error parseando JSON:', parseError);
        }
      } else {
        console.log('CartRecommendedProducts: No se encontró el script JSON en la sección');
      }
    } else {
      console.log('CartRecommendedProducts: Sección no encontrada en la respuesta');
    }
    
    return null;
  } catch (error) {
    console.log('CartRecommendedProducts: Error obteniendo recomendaciones:', error);
    return null;
  }
}

// Función para crear y mostrar productos recomendados dinámicamente
async function createAndShowRecommendedProducts() {
  console.log('CartRecommendedProducts: Creando productos recomendados dinámicamente...');
  
  try {
    // Verificar si ya existe el elemento en el DOM
    let container = document.getElementById('Cart-Drawer');
    if (!container) {
      console.log('CartRecommendedProducts: No se encontró Cart-Drawer');
      return;
    }
    
    // Verificar si ya existe el elemento cart-recommended-products
    let existingElement = container.querySelector('cart-recommended-products');
    if (existingElement) {
      console.log('CartRecommendedProducts: Elemento ya existe');
      return;
    }
    
    // Obtener el carrito actual
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    if (!cart.items || cart.items.length === 0) {
      console.log('CartRecommendedProducts: Carrito vacío');
      return;
    }
    
    const firstItem = cart.items[0];
    console.log('CartRecommendedProducts: Primer producto del carrito:', firstItem.title);
    
    // PRIMERO: Intentar obtener los productos del servidor (lógica de metafields via template)
    let filteredProducts = await getRecommendationsFromServer();
    
    if (filteredProducts && filteredProducts.length > 0) {
      console.log('CartRecommendedProducts: Usando productos del servidor (lógica de metafields):', filteredProducts.length);
    } else {
      // FALLBACK: Si no hay productos del servidor, usar la API de recomendaciones de Shopify
      console.log('CartRecommendedProducts: Usando fallback de recomendaciones de Shopify');
      
      const recommendationsResponse = await fetch(`/recommendations/products.json?product_id=${firstItem.product_id}&limit=10`);
      const recommendationsData = await recommendationsResponse.json();
      
      if (!recommendationsData.products || recommendationsData.products.length === 0) {
        console.log('CartRecommendedProducts: No se encontraron recomendaciones');
        return;
      }
      
      console.log('CartRecommendedProducts: Productos de Shopify encontrados:', recommendationsData.products.length);
      
      // Filtrar productos que ya están en el carrito y que estén disponibles
      const cartProductIds = cart.items.map(item => item.product_id);
      filteredProducts = recommendationsData.products.filter(p => 
        !cartProductIds.includes(p.id) && p.available
      ).slice(0, 10);
    }
    
    if (filteredProducts.length === 0) {
      console.log('CartRecommendedProducts: No hay productos disponibles después de filtrar');
      return;
    }
    
    console.log('CartRecommendedProducts: Productos finales a mostrar:', filteredProducts.length);
    
    // Crear el elemento HTML
    const html = `
      <cart-recommended-products 
        class="cart-recommended-products"
        data-dynamic="true"
      >
        <div class="cart-recommended-products__header">
          <h3 class="cart-recommended-products__title">También podría interesarte</h3>
          <div class="cart-recommended-products__navigation">
            <button class="cart-recommended-products__nav-button cart-recommended-products__nav-button--prev" aria-label="Anterior" disabled>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15,18 9,12 15,6"></polyline></svg>
            </button>
            <button class="cart-recommended-products__nav-button cart-recommended-products__nav-button--next" aria-label="Siguiente">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9,6 15,12 9,18"></polyline></svg>
            </button>
          </div>
        </div>
        <div class="cart-recommended-products__container" style="display: block;">
          <div class="cart-recommended-products__carousel-wrapper">
            <div class="cart-recommended-products__carousel products carousel" style="--desktop-columns: 3; --tablet-columns: 2;">
            </div>
          </div>
        </div>
      </cart-recommended-products>
    `;
    
    // Insertar después del contenido del carrito
    const cartContent = container.querySelector('.cart-drawer--content');
    if (cartContent) {
      cartContent.insertAdjacentHTML('afterend', html);
      console.log('CartRecommendedProducts: Elemento insertado en el DOM');
      
      // Esperar a que el elemento se inserte y renderizar productos
      setTimeout(() => {
        const element = container.querySelector('cart-recommended-products');
        if (element) {
          // Guardar productos y renderizar
          element.recommendedProducts = filteredProducts;
          element._initialized = true;
          
          // Renderizar productos directamente
          const carousel = element.querySelector('.cart-recommended-products__carousel');
          if (carousel) {
            carousel.innerHTML = '';
            
            filteredProducts.forEach(product => {
              // Manejar ambos formatos: servidor (template) y API de Shopify
              let price, comparePrice, variantId, imageUrl;
              
              if (product.variant_id) {
                // Formato del servidor (template cart.recommendations)
                price = product.price ? (parseFloat(product.price) / 100).toLocaleString('es-AR') : '';
                comparePrice = product.compare_at_price ? (parseFloat(product.compare_at_price) / 100).toLocaleString('es-AR') : null;
                variantId = product.variant_id;
                imageUrl = product.featured_image || '';
              } else {
                // Formato de la API de Shopify
                const firstVariant = product.variants?.[0];
                price = firstVariant?.price ? (parseFloat(firstVariant.price) / 100).toLocaleString('es-AR') : '';
                comparePrice = firstVariant?.compare_at_price ? (parseFloat(firstVariant.compare_at_price) / 100).toLocaleString('es-AR') : null;
                variantId = firstVariant?.id;
                imageUrl = product.featured_image ? product.featured_image.replace(/(\.[^.]+)$/, '_300x300$1') : '';
              }
              
              const slideHTML = `
                <div class="carousel__slide">
                  <div class="cart-recommended-product-card">
                    <div class="cart-recommended-product-card__image">
                      <a href="${product.url}" title="${product.title}">
                        <img src="${imageUrl}" alt="${product.title}" loading="lazy">
                      </a>
                    </div>
                    <div class="cart-recommended-product-card__info">
                      <a href="${product.url}" class="cart-recommended-product-card__title" title="${product.title}">
                        ${product.title}
                      </a>
                      <div class="cart-recommended-product-card__price">
                        ${comparePrice ? `<span class="price-compare">$${comparePrice}</span>` : ''}
                        <span class="price-current">$${price}</span>
                      </div>
                      <button type="button" class="button cart-recommended-product-card__button" 
                              data-variant-id="${variantId}">
                        Añadir al carrito
                      </button>
                    </div>
                  </div>
                </div>
              `;
              carousel.insertAdjacentHTML('beforeend', slideHTML);
            });
            
            // Agregar event listeners a los botones
            carousel.querySelectorAll('.cart-recommended-product-card__button').forEach(button => {
              button.addEventListener('click', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const variantId = button.dataset.variantId;
                if (!variantId) return;
                
                button.disabled = true;
                const originalText = button.textContent;
                button.textContent = 'Agregando...';
                
                try {
                  const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
                  });
                  
                  if (response.ok) {
                    button.textContent = '¡Agregado!';
                    // Disparar evento de actualización del carrito
                    document.dispatchEvent(new CustomEvent('cart:refresh'));
                  } else {
                    button.textContent = 'Error';
                  }
                  
                  setTimeout(() => {
                    button.textContent = originalText;
                    button.disabled = false;
                  }, 1500);
                } catch (error) {
                  console.error('Error al agregar al carrito:', error);
                  button.textContent = 'Error';
                  setTimeout(() => {
                    button.textContent = originalText;
                    button.disabled = false;
                  }, 1500);
                }
              });
            });
            
            // Inicializar navegación del carrusel
            if (element.initCarouselNavigation) {
              element.initCarouselNavigation();
            }
            
            console.log('CartRecommendedProducts: Productos renderizados exitosamente');
          }
        }
      }, 100);
    }
  } catch (error) {
    console.error('CartRecommendedProducts: Error:', error);
  }
}

// Escuchar eventos de actualización del carrito
document.addEventListener('cart:item-added', () => {
  console.log('CartRecommendedProducts: Evento cart:item-added detectado');
  // Esperar a que el cart-drawer se abra y luego crear el carrusel
  setTimeout(async () => {
    const elements = document.querySelectorAll('cart-recommended-products');
    if (elements.length === 0) {
      console.log('CartRecommendedProducts: No hay elemento, creando dinámicamente...');
      await createAndShowRecommendedProducts();
    } else {
      console.log('CartRecommendedProducts: Elemento ya existe, inicializando...');
      initCartRecommendedElements();
    }
  }, 500);
});

document.addEventListener('cart:refresh', () => {
  console.log('CartRecommendedProducts: Evento cart:refresh detectado');
  setTimeout(async () => {
    const elements = document.querySelectorAll('cart-recommended-products');
    if (elements.length === 0) {
      await createAndShowRecommendedProducts();
    } else {
      initCartRecommendedElements();
    }
  }, 500);
});

// Escuchar eventos adicionales que podrían indicar actualización del carrito
document.addEventListener('line-item:change:end', () => {
  console.log('CartRecommendedProducts: Evento line-item:change:end detectado');
  setTimeout(initCartRecommendedElements, 300);
});

// Inicializar elementos existentes
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('CartRecommendedProducts: DOMContentLoaded');
    initCartRecommendedElements();
  });
} else {
  console.log('CartRecommendedProducts: Script cargado, DOM ya listo');
  initCartRecommendedElements();
}
